import React, { useState, useEffect } from "react";
import { Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import axios from "axios";
import { getBackendUrl } from "../../../config";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 0, 4) },
  label: {
    textAlign: "center",
    color: "#58EFFE",
    fontSize: "0.76rem",
    fontWeight: 700,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.2),
  },
  title: {
    textAlign: "center",
    color: "#ffffff",
    fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    margin: 0,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    textAlign: "center",
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.95rem",
    marginBottom: theme.spacing(4),
  },
  currencyNote: {
    textAlign: "center",
    color: "rgba(255,255,255,0.3)",
    fontSize: "0.75rem",
    marginBottom: theme.spacing(3),
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, 1fr)" },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 22,
    border: "1px solid rgba(255, 255, 255, 0.06)",
    background: "rgba(255, 255, 255, 0.02)",
    backdropFilter: "blur(8px)",
    transition: "all 0.35s ease",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      borderColor: "rgba(255, 255, 255, 0.12)",
      transform: "translateY(-4px)",
      boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
    },
  },
  featured: {
    border: "1px solid rgba(88, 239, 254, 0.25)",
    background: "rgba(88, 239, 254, 0.03)",
    position: "relative",
    boxShadow: "0 0 40px rgba(88, 239, 254, 0.06)",
    "&::before": {
      content: '"POPULAR"',
      position: "absolute",
      top: -12,
      left: "50%",
      transform: "translateX(-50%)",
      padding: "4px 16px",
      borderRadius: 999,
      fontSize: "0.65rem",
      fontWeight: 800,
      letterSpacing: "0.12em",
      background: "linear-gradient(135deg, #58EFFE, #39F3CC)",
      color: "#000000",
      boxShadow: "0 4px 16px rgba(88, 239, 254, 0.3)",
    },
  },
  planName: {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "0.82rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: theme.spacing(0.5),
  },
  price: {
    fontSize: "clamp(2rem, 4vw, 2.6rem)",
    fontWeight: 800,
    color: "#ffffff",
    lineHeight: 1,
    marginBottom: theme.spacing(0.3),
  },
  priceUsd: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.3)",
    marginBottom: theme.spacing(0.5),
  },
  priceUnit: {
    fontSize: "0.8rem",
    fontWeight: 400,
    color: "rgba(255, 255, 255, 0.3)",
  },
  desc: {
    color: "rgba(255, 255, 255, 0.4)",
    fontSize: "0.82rem",
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
    minHeight: 40,
  },
  trialBadge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 999,
    fontSize: "0.7rem",
    fontWeight: 700,
    background: "rgba(57, 243, 204, 0.15)",
    color: "#39F3CC",
    marginBottom: theme.spacing(1.5),
    border: "1px solid rgba(57, 243, 204, 0.2)",
  },
  sectionLabel: {
    color: "rgba(255,255,255,0.3)",
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  features: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.6),
    flex: 1,
  },
  feature: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.8rem",
  },
  featureIcon: { fontSize: 14, color: "#39F3CC", flex: "0 0 auto" },
  featureIconOff: { fontSize: 14, color: "rgba(255,255,255,0.15)", flex: "0 0 auto" },
  featureOff: { color: "rgba(255,255,255,0.2)", textDecoration: "line-through" },
  highlight: {
    color: "#58EFFE",
    fontWeight: 600,
  },
  ctaPrimary: {
    width: "100%",
    textTransform: "none",
    fontWeight: 700,
    color: "#000000",
    borderRadius: 14,
    padding: theme.spacing(1.2, 2),
    fontSize: "0.92rem",
    background: "linear-gradient(135deg, #58EFFE 0%, #39F3CC 100%)",
    boxShadow: "0 4px 20px rgba(88, 239, 254, 0.25)",
    transition: "all 0.3s ease",
    marginTop: "auto",
    "&:hover": {
      background: "linear-gradient(135deg, #3dd4e8 0%, #2dd8b0 100%)",
      boxShadow: "0 6px 28px rgba(88, 239, 254, 0.4)",
    },
  },
  ctaSecondary: {
    width: "100%",
    textTransform: "none",
    fontWeight: 600,
    color: "rgba(255, 255, 255, 0.7)",
    borderRadius: 14,
    padding: theme.spacing(1.1, 2),
    fontSize: "0.92rem",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    background: "rgba(255, 255, 255, 0.03)",
    transition: "all 0.3s ease",
    marginTop: "auto",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.06)",
      borderColor: "rgba(255, 255, 255, 0.2)",
      color: "#ffffff",
    },
  },
  loading: { textAlign: "center", padding: theme.spacing(6) },
}));

const FEATURE_LABELS = {
  whatsapp: "WhatsApp",
  facebook: "Facebook Messenger",
  instagram: "Instagram DM",
  campaigns: "Campanas masivas",
  schedules: "Agendamiento",
  internalChat: "Chat interno",
  externalApi: "API REST & Webhooks",
  kanban: "Kanban de leads",
  openAi: "IA Claude (Sonnet)",
  integrations: "Integraciones",
  googleCalendar: "Google Calendar",
};

export default function PricingSection({ resolveText, onLogin }) {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const backendUrl = getBackendUrl();
        const resp = await axios.get(`${backendUrl}/geo-pricing`);
        setData(resp.data);
      } catch (e) {
        // Fallback: try without backend URL
        try {
          const resp = await axios.get("/geo-pricing");
          setData(resp.data);
        } catch (_) {}
      }
      setLoading(false);
    };
    fetchPricing();
  }, []);

  const formatPrice = (plan) => {
    if (!data) return `$${plan.priceUsd}`;
    const sym = data.symbol || "$";
    const local = plan.priceLocal;
    if (data.currency === "USD") return `$${plan.priceUsd}`;
    return `${sym}${Number(local).toLocaleString()}`;
  };

  const featuredIndex = 1; // Profesional = index 1

  if (loading) {
    return (
      <section className={classes.root} id="pricing">
        <div className={classes.loading}>
          <CircularProgress style={{ color: "#58EFFE" }} />
        </div>
      </section>
    );
  }

  const plans = data?.plans || [];

  return (
    <section className={`${classes.root} gsap-fade-in`} id="pricing">
      <p className={classes.label}>Planes y precios</p>
      <h2 className={classes.title}>Elige el plan perfecto para tu negocio</h2>
      <p className={classes.subtitle}>
        Todos los planes incluyen IA Claude Sonnet y soporte en espanol
      </p>
      {data && data.currency !== "USD" && (
        <p className={classes.currencyNote}>
          Precios en {data.currency} ({data.symbol}). Precio base en USD.
        </p>
      )}

      <div className={classes.grid}>
        {plans.map((plan, idx) => (
          <div
            key={plan.id}
            className={`${classes.card} ${idx === featuredIndex ? classes.featured : ""}`}
          >
            <div className={classes.planName}>{plan.name}</div>
            <div className={classes.price}>
              {formatPrice(plan)}
              <span className={classes.priceUnit}>/mes</span>
            </div>
            {data && data.currency !== "USD" && (
              <div className={classes.priceUsd}>USD ${plan.priceUsd}/mes</div>
            )}
            <p className={classes.desc}>{plan.description}</p>

            {plan.trialDays > 0 && (
              <div className={classes.trialBadge}>
                {plan.trialDays} dias gratis de prueba
              </div>
            )}

            {/* Limits */}
            <div className={classes.sectionLabel}>Incluye</div>
            <ul className={classes.features}>
              <li className={classes.feature}>
                <CheckCircleIcon className={classes.featureIcon} />
                <span className={classes.highlight}>{plan.users}</span>&nbsp;
                {plan.users === 1 ? "agente" : "agentes"}
              </li>
              <li className={classes.feature}>
                <CheckCircleIcon className={classes.featureIcon} />
                <span className={classes.highlight}>
                  {plan.maxContacts >= 99999 ? "Ilimitados" : plan.maxContacts.toLocaleString()}
                </span>
                &nbsp;contactos
              </li>
              <li className={classes.feature}>
                <CheckCircleIcon className={classes.featureIcon} />
                <span className={classes.highlight}>
                  {plan.aiTokensIncluded.toLocaleString()}
                </span>
                &nbsp;mensajes IA/mes
              </li>
              {plan.maxCampaignsPerDay > 0 && (
                <li className={classes.feature}>
                  <CheckCircleIcon className={classes.featureIcon} />
                  {plan.maxCampaignsPerDay >= 99999
                    ? "Envios ilimitados"
                    : `${plan.maxCampaignsPerDay.toLocaleString()} envios/dia`}
                </li>
              )}

              {/* Feature toggles */}
              {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                const enabled = plan.features?.[key];
                return (
                  <li
                    key={key}
                    className={`${classes.feature} ${!enabled ? classes.featureOff : ""}`}
                  >
                    {enabled ? (
                      <CheckCircleIcon className={classes.featureIcon} />
                    ) : (
                      <CancelIcon className={classes.featureIconOff} />
                    )}
                    {label}
                  </li>
                );
              })}

              <li className={classes.feature}>
                <CheckCircleIcon className={classes.featureIcon} />
                Soporte: {plan.supportLevel === "dedicado"
                  ? "Dedicado + onboarding"
                  : plan.supportLevel === "whatsapp"
                  ? "WhatsApp directo"
                  : plan.supportLevel === "chat"
                  ? "Chat prioritario"
                  : "Email"}
              </li>
            </ul>

            <Button
              className={idx === featuredIndex ? classes.ctaPrimary : classes.ctaSecondary}
              startIcon={<ExitToAppIcon fontSize="small" />}
              onClick={onLogin}
            >
              {plan.trialDays > 0
                ? `Probar ${plan.trialDays} dias gratis`
                : "Comenzar ahora"}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
